import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import CategoryNavigationItem from '@components/vitals/CategoryNavigationItem';
import { ProductData } from '@data/storefront';
import NavigationChevron from '@images/vitals-navigation-selected.svg';
import useLanguageData from '@utils/useLanguageData';
import useNavigate from '@utils/useNavigate';
import useDesktop from './useDesktop';

const MOBILE_BREAKPOINT = 1024;

interface Props {
  products: ProductData[];
  categories: { data: { title: string }; uid: string; lang: string }[];
}

const Filters: React.FC<Props> = ({ products, categories }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const location = useLocation();
  const { filterNodesByLanguage } = useLanguageData(categories);
  const filteredCategories = filterNodesByLanguage();
  const desktop = useDesktop(MOBILE_BREAKPOINT);
  const [pageTitle, setPageTitle] = useState<string>(t('components.all-products'));
  const link = '/products/';
  const [showSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNavigation, setShowNavigation] = useState(false);

  useEffect(() => {
    const currentCategory = filteredCategories.find((category) => location.pathname.includes(category.uid));
    if (currentCategory) {
      setPageTitle(currentCategory.data.title);
    }
  }, [filteredCategories]);

  const toggleNavigation = () => {
    setShowNavigation(!showNavigation);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const pressedEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      goToSearchResults();
    }
  };

  const goToSearchResults = () => {
    navigateWrapper(`/search-results/?${searchTerm}`);
  };

  const getCategoryCount = (uid = '') => {
    if (uid === '') {
      return products.length;
    }

    const categoryProducts = products.filter((item) => {
      const itemCategories =
        item.product_categories && item.product_categories.text !== '' ? item.product_categories.text?.split(',') : [item.product_category.uid];
      return itemCategories?.includes(uid);
    });

    return categoryProducts.length;
  };

  return (
    <Container>
      <MobileControls>
        <CurrentPage onClick={toggleNavigation} $show={!showSearch}>
          {pageTitle}
        </CurrentPage>
        <NavButton onClick={toggleNavigation} $show={!showSearch} $active={showNavigation}>
          <span>{t('components.categories')}</span>
          <NavigationChevron className="chevron" />
        </NavButton>
        <StyledInput
          $show={showSearch}
          type="text"
          placeholder={t('components.search')}
          value={searchTerm}
          onKeyPress={pressedEnter}
          onChange={handleChange}
        />
      </MobileControls>
      <CategoryNavigationWrap $show={desktop ? !showSearch : showNavigation}>
        <CategoryNavigationItem link={link} count={getCategoryCount()} title={t('components.all-products')} />
        {filteredCategories.map((category, i) => (
          <CategoryNavigationItem
            link={`${link}${category.uid}/`}
            count={getCategoryCount(category.uid)}
            title={category?.data?.title ?? t('components.all-products')}
            partiallyActive={true}
            key={i}
          />
        ))}
      </CategoryNavigationWrap>
    </Container>
  );
};

export default Filters;

interface StyleProps {
  $show?: boolean;
  $type?: string;
  $placeholder?: string;
  $value?: string;
  $active?: boolean;
}

const Container = styled.div`
  margin: 100px 0 60px;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  min-height: 50px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 102px 0 30px;
    flex-direction: column;
    background: #fdf7e3;
    border-bottom: 1px solid ${Colors.ecru};
  }

  @media (max-width: 768px) {
    margin: 70px 0 30px;
  }
`;

const MobileControls = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    border-top: 1px solid ${Colors.ecru};
    border-bottom: 1px solid ${Colors.ecru};
  }
`;

const NavButton = styled.button<StyleProps>`
  border: 0;
  background: transparent;
  color: ${Colors.forestGreen};
  height: 48px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  display: ${(props) => (props.$show ? 'flex' : 'none')};

  &:hover {
    opacity: 0.7;
  }

  span {
    font-family: 'BrownStd';
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: inherit;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  svg.chevron {
    width: 6px;
    height: 10px;
    margin-left: 6px;

    path {
      &:first-child {
        fill: ${Colors.forestGreen};
      }
      &:last-child {
        stroke: ${Colors.forestGreen};
      }
    }
    transform: ${(props) => (props.$active ? 'rotate(-90deg)' : 'rotate(90deg)')};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 50px;
    margin-right: 20px;
  }
`;

const StyledInput = styled.input<StyleProps>`
  padding: 0 20px;
  height: 48px;
  flex-grow: 1;
  border: none;
  border-radius: 0;
  background: ${Colors.cream};
  width: 50%;
  color: ${Colors.darkGreen};
  font-family: 'BrownStd';
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;

  display: ${(props) => (props.$show ? 'flex' : 'none')};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${Colors.darkGreen}50;
  }

  &:-ms-input-placeholder {
    color: ${Colors.darkGreen}50;
  }

  &::-ms-input-placeholder {
    color: ${Colors.darkGreen}50;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 50px;
  }
`;

const CurrentPage = styled(P)<StyleProps>`
  height: 50px;
  line-height: 50px;
  cursor: pointer;
  flex-grow: 1;
  padding-left: 20px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0;
  display: ${(props) => (props.$show ? 'flex' : 'none')};
`;

const CategoryNavigationWrap = styled.div<StyleProps>`
  flex-direction: row;
  align-items: center;
  display: ${(props) => (props.$show ? 'flex' : 'none')};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
    margin: 30px 0;
    padding: 0 40px;
    width: 100%;
  }
`;
